import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, SlidersHorizontal } from 'lucide-react';
import Header from '../components/Header';
import TradeCard from '../components/TradeCard';
import TradesMap from '../components/TradesMap';
import SmartSearchHelper from '../components/SmartSearchHelper';
import { api } from '../lib/api';

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [suburb, setSuburb] = useState(params.get('suburb') || '');
  const [category, setCategory] = useState(params.get('category') || '');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.categories().then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (suburb) p.suburb = suburb;
    if (category) p.category = category;
    if (availableOnly) p.available_today = true;
    if (emergencyOnly) p.emergency = true;
    api.listTrades(p).then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, [q, suburb, category, availableOnly, emergencyOnly]);

  const updateUrl = () => {
    const p = {};
    if (q) p.q = q;
    if (suburb) p.suburb = suburb;
    if (category) p.category = category;
    setParams(p);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="search-page">
      <Header />

      {/* Filter bar */}
      <div className="border-b border-[#E5E4E0] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-5 md:px-10">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex flex-1 items-center gap-2 border border-[#E5E4E0] bg-white px-3 py-2">
              <Search size={16} className="text-[#8A8988]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onBlur={updateUrl}
                placeholder="What do you need done?"
                data-testid="search-q-input"
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#8A8988]"
              />
            </div>
            <div className="flex items-center gap-2 border border-[#E5E4E0] bg-white px-3 py-2 md:w-72">
              <MapPin size={16} className="text-[#8A8988]" />
              <input
                value={suburb}
                onChange={(e) => setSuburb(e.target.value)}
                onBlur={updateUrl}
                placeholder="Suburb"
                data-testid="search-suburb-input"
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#8A8988]"
              />
            </div>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setTimeout(updateUrl, 0); }}
              data-testid="category-select"
              className="border border-[#E5E4E0] bg-white px-3 py-2 text-sm outline-none focus:border-[#1A362D]"
            >
              <option value="">All trades</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-[#5C5B5A]">
              <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} data-testid="available-only-checkbox" className="accent-[#1A362D]" />
              Available today
            </label>
            <label className="flex items-center gap-2 text-sm text-[#5C5B5A]">
              <input type="checkbox" checked={emergencyOnly} onChange={(e) => setEmergencyOnly(e.target.checked)} data-testid="emergency-only-checkbox" className="accent-[#E76F51]" />
              <span className="inline-flex items-center gap-1">⚡ 24/7 only</span>
            </label>
          </div>

          {/* Smart Search Helper — inclusive plain-English matcher */}
          <SmartSearchHelper query={q} suburb={suburb} />
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 py-8 md:px-10 lg:grid-cols-[1.15fr,1fr]">
        <div>
          <div className="mb-4 flex items-baseline justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Results</div>
            <div className="text-sm text-[#5C5B5A]" data-testid="results-count">{results.length} verified trades</div>
          </div>
          {loading ? (
            <div className="border border-[#E5E4E0] bg-white p-12 text-center text-[#8A8988]">Loading...</div>
          ) : results.length === 0 ? (
            <div className="border border-[#E5E4E0] bg-white p-12 text-center" data-testid="no-results">
              <div className="font-heading text-2xl font-bold text-[#1C1B1A]">No matches yet</div>
              <p className="mt-2 text-[#5C5B5A]">Try widening your suburb or removing filters. Or use our AI helper on the home page.</p>
              <Link to="/" className="btn-primary mt-6 inline-flex">Back to home</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="results-list">
              {results.map((t) => <TradeCard key={t.id} trade={t} />)}
            </div>
          )}
        </div>
        <div className="hidden lg:block">
          <div className="sticky top-24 h-[calc(100vh-8rem)]">
            <TradesMap trades={results} />
          </div>
        </div>
      </div>
    </div>
  );
}
